import { useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Alert, Tabs, Tab,
  CircularProgress, InputAdornment, IconButton, Divider
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LangContext';
import { LangSwitcher } from '../components/LangSwitcher';

export function LoginPage({ verifiedAlert }: { verifiedAlert?: boolean }) {
  const { login } = useAuth();
  const { T, lang } = useLang();
  const zh = lang === 'zh';
  const [tab, setTab] = useState(0);
  // tab: 0=login, 1=register, 2=forgot-password
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(verifiedAlert ? T.verifyEmailSent : '');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try { await login(email, password); }
    catch (err) { setError(err instanceof Error ? err.message : 'Login failed'); }
    finally { setLoading(false); }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, displayName }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setSuccess(T.verifyEmailSent);
      setTab(0);
    } catch (err) { setError(err instanceof Error ? err.message : 'Registration failed'); }
    finally { setLoading(false); }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setSuccess(zh ? '若此信箱已註冊，密碼重置郵件已寄出，請查收。' : 'If this email is registered, you will receive a reset link shortly.');
    } catch (err) { setError(err instanceof Error ? err.message : 'Request failed'); }
    finally { setLoading(false); }
  };

  const features = [T.feature1, T.feature2, T.feature3, T.feature4];

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', background: 'linear-gradient(135deg, #bf360c 0%, #e65100 50%, #ff6d00 100%)', position: 'relative' }}>
      {/* Language switcher top-right */}
      <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 10 }}>
        <LangSwitcher light />
      </Box>

      {/* Left branding panel */}
      <Box sx={{ display: { xs: 'none', md: 'flex' }, flex: 1, flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 6, color: '#fff' }}>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <img src="/logo.png" alt="DataBrushing AI" style={{ width: 64, height: 64, borderRadius: 8, objectFit: 'contain', background: '#fff', padding: 4 }} />
          <Box>
            <Typography variant="caption" sx={{ display: 'block', letterSpacing: 1, fontSize: 12, textShadow: '0 1px 4px rgba(0,0,0,0.35)', opacity: 0.9 }}>DataBrushing AI</Typography>
            <Typography variant="h4" fontWeight={900} sx={{ lineHeight: 1.1, textShadow: '0 2px 10px rgba(0,0,0,0.4)' }}>PQC Navigator</Typography>
          </Box>
        </Box>
        <Typography variant="h6" sx={{ textAlign: 'center', maxWidth: 360, lineHeight: 1.7, mb: 1, fontWeight: 600, textShadow: '0 1px 6px rgba(0,0,0,0.35)' }}>
          {T.platformSubtitle}
        </Typography>
        <Typography variant="body2" sx={{ textAlign: 'center', mb: 4, textShadow: '0 1px 4px rgba(0,0,0,0.3)', opacity: 0.85 }}>
          {T.heroCaption}
        </Typography>
        <Divider sx={{ my: 3, width: 60, borderColor: 'rgba(255,255,255,0.3)' }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', maxWidth: 300 }}>
          {features.map(f => (
            <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.8)', flexShrink: 0 }} />
              <Typography variant="body1" sx={{ textShadow: '0 1px 4px rgba(0,0,0,0.3)', fontWeight: 500 }}>{f}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Right form panel */}
      <Box sx={{ flex: { xs: 1, md: '0 0 440px' }, display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 2, md: 4 }, bgcolor: 'background.default' }}>
        <Paper sx={{ p: { xs: 3, md: 4 }, width: '100%', maxWidth: 400 }}>
          {/* Mobile logo */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1, mb: 3 }}>
            <img src="/logo.png" alt="DataBrushing AI" style={{ width: 36, height: 36, borderRadius: 6, objectFit: 'contain' }} />
            <Typography variant="h5" fontWeight={800} color="primary">PQC Navigator</Typography>
          </Box>

          <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
            {tab === 0 ? T.welcomeBack : T.registerTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {tab === 0 ? T.loginSubtitle : T.registerSubtitle}
          </Typography>

          <Tabs value={tab} onChange={(_, v) => { setTab(v); setError(''); setSuccess(''); }}
            sx={{ mb: 3, '& .MuiTab-root': { fontWeight: 600 } }}>
            <Tab label={T.login} />
            <Tab label={T.register} />
            <Tab label={zh ? '忘記密碼' : 'Forgot Password'} />
          </Tabs>

          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{success}</Alert>}

          {tab === 0 ? (
            <form onSubmit={handleLogin}>
              <TextField fullWidth label={T.email} value={email}
                onChange={e => setEmail(e.target.value)} sx={{ mb: 2 }} required
                InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlinedIcon fontSize="small" color="action" /></InputAdornment> }} />
              <TextField fullWidth label={T.password} value={password}
                onChange={e => setPassword(e.target.value)} sx={{ mb: 1 }} required
                type={showPwd ? 'text' : 'password'} autoComplete="current-password"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><LockOutlinedIcon fontSize="small" color="action" /></InputAdornment>,
                  endAdornment: <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPwd(v => !v)}>
                      {showPwd ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>,
                }} />
              {/* Forgot password link */}
              <Box sx={{ textAlign: 'right', mb: 2 }}>
                <Button size="small" variant="text" onClick={() => { setTab(2); setError(''); setSuccess(''); }}
                  sx={{ color: 'text.secondary', fontSize: 12, p: 0, minWidth: 0, textTransform: 'none' }}>
                  {zh ? '忘記密碼？' : 'Forgot password?'}
                </Button>
              </Box>
              <Button fullWidth variant="contained" type="submit" disabled={loading} size="large" sx={{ py: 1.5 }}>
                {loading ? <CircularProgress size={22} color="inherit" /> : T.loginBtn}
              </Button>
            </form>
          ) : tab === 1 ? (
            <form onSubmit={handleRegister}>
              <TextField fullWidth label="Email" value={email}
                onChange={e => setEmail(e.target.value)} sx={{ mb: 2 }} required type="email"
                InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlinedIcon fontSize="small" color="action" /></InputAdornment> }} />
              <TextField fullWidth label={T.displayName} value={displayName}
                onChange={e => setDisplayName(e.target.value)} sx={{ mb: 2 }} />
              <TextField fullWidth label={T.passwordHint} value={password}
                onChange={e => setPassword(e.target.value)} sx={{ mb: 3 }} required
                type={showPwd ? 'text' : 'password'} inputProps={{ minLength: 6 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><LockOutlinedIcon fontSize="small" color="action" /></InputAdornment>,
                  endAdornment: <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPwd(v => !v)}>
                      {showPwd ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>,
                }} />
              <Button fullWidth variant="contained" type="submit" disabled={loading} size="large" sx={{ py: 1.5 }}>
                {loading ? <CircularProgress size={22} color="inherit" /> : T.registerBtn}
              </Button>
            </form>
          ) : (
            /* Forgot Password Tab */
            <form onSubmit={handleForgotPassword}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {zh
                  ? '請輸入您的註冊 Email，我們將寄送密碼重置連結（有效期 1 小時）。'
                  : 'Enter your registered email and we\'ll send a password reset link (valid for 1 hour).'}
              </Typography>
              <TextField fullWidth label={T.email} value={email}
                onChange={e => setEmail(e.target.value)} sx={{ mb: 3 }} required type="email"
                InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlinedIcon fontSize="small" color="action" /></InputAdornment> }} />
              <Button fullWidth variant="contained" type="submit" disabled={loading} size="large" sx={{ py: 1.5 }}>
                {loading ? <CircularProgress size={22} color="inherit" /> : (zh ? '寄送重置連結' : 'Send Reset Link')}
              </Button>
              <Button fullWidth variant="text" size="small" onClick={() => { setTab(0); setError(''); setSuccess(''); }}
                sx={{ mt: 1.5, color: 'text.secondary', textTransform: 'none' }}>
                {zh ? '← 返回登入' : '← Back to Login'}
              </Button>
            </form>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
